import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { imageManager, ImageNotAvailableError } from "@services/image-manager";

/**
 * In-memory cache for storing image Blob URLs.
 * The key is the image source (src), and the value is the object URL.
 */
const memoryCache = new Map();

/**
 * Maximum number of items allowed in the cache.
 * When the limit is reached, the oldest entry is removed.
 */
const MAX_CACHE_SIZE = 100;

/**
 * Sources that could not be loaded, e.g. a thumbnail URL whose blob does not exist.
 * Without it every re-mount retries the same failing request, which happens constantly
 * while the data grid virtualizes its rows during scrolling.
 *
 * Deliberately not size-limited: entries are URL strings, bounded by the distinct
 * thumbnails seen in one session, and evicting them would let the failing URLs be
 * requested again.
 */
const failedSources = new Set();
/**
 * Adds an image URL to the in-memory cache.
 * If the cache exceeds the maximum size, the oldest entry is removed.
 */
function addToCache(src, url) {
  if (memoryCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = memoryCache.keys().next().value;
    if (oldestKey) {
      const oldestUrl = memoryCache.get(oldestKey);
      if (oldestUrl) {
        URL.revokeObjectURL(oldestUrl);
      }
      memoryCache.delete(oldestKey);
    }
  }
  memoryCache.set(src, url);
}

/**
 * Custom React hook to lazily load and manage an image as a Blob URL.
 *
 * This hook:
 * - Checks if the image is already cached in memory.
 * - If not cached, fetches the image Blob using `imageManager`, creates an object URL, and stores it in the cache.
 * - Cleans up the object URL when the component unmounts (if not cached).
 *
 * Why both in-memory cache and browser storage?
 * - In-memory cache: ultra-fast access during the current SPA session, avoiding repeated API calls.
 * - Browser storage: persistence across page refreshes or new sessions, reducing network usage.
 *
 * Memory lifecycle:
 * - Cache is cleared on page refresh or tab close.
 * - Limited to 100 images to prevent excessive memory usage.
 */
export function useLazyImage(src, companyId, contextType, contextId) {
  const $ = _c(13);
  let t0;
  if ($[0] !== src) {
    t0 = memoryCache.get(src) ?? null;
    $[0] = src;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [imageUrl, setImageUrl] = useState(t0);
  let t1;
  if ($[2] !== src) {
    t1 = failedSources.has(src);
    $[2] = src;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [hasError, setHasError] = useState(t1);
  let t2;
  let t3;
  if ($[4] !== companyId || $[5] !== contextId || $[6] !== contextType || $[7] !== src) {
    t2 = () => {
      let objectUrl = memoryCache.get(src) ?? null;
      const loadImage = async function loadImage() {
        if (failedSources.has(src)) {
          setHasError(true);
          setImageUrl(null);
          return;
        }
        ;
        try {
          if (!objectUrl) {
            const blob = await imageManager.getImageBlob(src, companyId, contextType, contextId);
            objectUrl = URL.createObjectURL(blob);
            addToCache(src, objectUrl);
          }
          setHasError(false);
          setImageUrl(objectUrl);
        } catch (t4) {
          const error = t4;
          if (error instanceof ImageNotAvailableError) {
            failedSources.add(src);
          }
          setHasError(true);
          setImageUrl(null);
        }
      };
      loadImage();
      return () => {
        if (objectUrl && !memoryCache.has(src)) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    };
    t3 = [src, companyId, contextType, contextId];
    $[4] = companyId;
    $[5] = contextId;
    $[6] = contextType;
    $[7] = src;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== hasError || $[11] !== imageUrl) {
    t4 = {
      imageUrl,
      hasError
    };
    $[10] = hasError;
    $[11] = imageUrl;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}